import { Component, Element, h, Prop, State, Event, EventEmitter, Method, Host, Listen, AttachInternals } from '@stencil/core';
import { v4 as uuidv4 } from 'uuid';
import { EventDetail, generateEventDetailPayload } from '../../utils';

@Component({
  tag: 'anchor-textarea',
  styleUrl: 'textarea.scss',
  shadow: true,
  formAssociated: true,
})
export class AnchorTextarea {
  @Element() host: HTMLElement;

  @AttachInternals() internals: ElementInternals;

  textareaElm!: HTMLTextAreaElement;

  /**
   * The value in the textarea field. Use this to set a value and read it
   */
  @Prop() value: string = '';

  /**
   * A unique identifier for the textarea component.
   * */
  @Prop({ mutable: true, reflect: true }) uuid?: string = `anchor-textarea-${uuidv4()}`;

  /**
   * The name of the textarea field
   */
  @Prop() name?: string = undefined;

  /**
   * The placeholder text of the textarea field
   */
  @Prop() placeholder?: string = undefined;

  /**
   * The label text of the textarea field
   */
  @Prop() label?: string = undefined;

  /**
   * This prop decides if the messages should render between the label of an textarea and the textarea field, or below the textarea field.
   *
   * @type {'inside' | 'below'}
   * @default 'below'
   */
  @Prop({ reflect: true }) messagePlacement: 'inside' | 'below' = 'inside';

  /**
   * The maximum length of the textarea field
   */
  @Prop() maxlength?: number = undefined;

  /**
   * This prop decides if the textarea field should be resizable or not
   */
  @Prop() noResize: boolean = false;

  /**
   * This prop decides if the textarea field should be disabled or not
   */
  @Prop() disabled: boolean = false;

  /**
   * This prop decides if the textarea field should be readonly or not
   */
  @Prop() readonly: boolean = false;

  /**
   * This prop decides if the textarea field is required or not
   */
  @Prop() required: boolean = false;

  /**
   * Set the autocomplete attribute for the textare field
   */
  @Prop() autocomplete: string;

  /**
   * This prop decides if the textarea field should be autofocused or not
   */
  @Prop() autofocus: boolean = false;

  /**
   * This prop decides if the textarea field should be spellchecked or not
   */
  @Prop() spellCheck: 'false' | 'true' = 'false';

  /**
   * This prop decides if the textarea field should be autocorrected or not
   */
  @Prop() autocorrect?: 'on' | 'off' = 'off';

  /**
   * Show an error message
   */
  @Prop() error: string = '';

  /**
   * Show a help text
   */
  @Prop() helpText?: string = undefined;

  /**
   * This prop decides if all content inside the textarea should be selected when it gets focus
   */
  @Prop({ reflect: true }) selectOnFocus: boolean;

  // Aria attributes

  /**
   * aria-live attribute for the textarea field
   */
  @Prop() ariaLive: string = undefined;

  /**
   * aria-describedby attribute for the textarea field
   */
  @Prop() ariaDescribedBy?: string;

  // States

  @State() hasErrorSlotActive: boolean = false;

  @State() charcount: string | number = '';

  // Computed properties
  get focused() {
    return this.textareaElm === document.activeElement;
  }

  get hasError() {
    return (this.error && this.error.length > 0) || this.hasErrorSlotActive;
  }

  // Events
  /**
   * Event emitted when the user is focusing the textarea field
   */
  @Event() focusevent: EventEmitter;

  /**
   * Event emitted when the user blurs the textarea field
   */
  @Event() blurevent: EventEmitter;

  /**
   * Event emitted when the user clears the textarea field
   */
  @Event() clearevent: EventEmitter<EventDetail>;

  /**
   *  Event emitted when the textarea value is changed. Only triggers when focus leaves the textarea element
   */
  @Event() inputChange: EventEmitter<EventDetail>;

  /**
   * Event emitted when the user is inputting something into the input field
   */
  @Event() inputEvent: EventEmitter<EventDetail>;

  /**
   * Listen for input events on the textarea element
   */
  @Listen('input')
  handleInput(event) {
    if (!this.textareaElm.value.length) {
      this.clearevent.emit(generateEventDetailPayload(this.textareaElm, this.textareaElm.value, event));
    }
    this.internals.setFormValue(this.textareaElm.value);
    this.inputEvent.emit(generateEventDetailPayload(this.textareaElm, this.textareaElm.value, event));
  }

  // Methods
  getCurrentlength() {
    this.charcount = (this.textareaElm.value ?? '').toString().length;
  }

  handleChange(event: Event) {
    this.inputChange.emit(generateEventDetailPayload(this.textareaElm, this.textareaElm.value, event));
  }

  @Method()
  async focusTextarea() {
    setTimeout(() => {
      this.textareaElm.focus();
    }, 100);
  }

  handleFocus(e: FocusEvent) {
    this.focusevent.emit(e);
    if (this.selectOnFocus) {
      this.textareaElm.setSelectionRange(0, (this.textareaElm.value ?? '').toString().length);
    }
  }

  // Lifecycle events
  componentWillLoad() {
    this.hasErrorSlotActive = !!this.host.querySelector("[slot='error-msg']");
  }

  componentDidRender() {
    this.getCurrentlength();
  }

  render() {
    const textareaContainerClass = {
      'anchor-c-textarea__container': true,
      'anchor-c-textarea__container--disabled': this.disabled,
      'anchor-c-textarea__container--focused': this.focused,
      'anchor-c-textarea__container--error': this.hasError,
      'anchor-c-textarea__container--help': this.helpText && typeof this.helpText !== 'undefined',
      'anchor-c-textarea__container--no-resize': this.noResize,
    };

    const textareaClass = {
      'anchor-c-textarea': true,
      'anchor-c-textarea--disabled': this.disabled,
      'anchor-c-textarea--readonly': this.readonly,
      'anchor-c-textarea--error': this.hasError,
      'anchor-c-textarea--help': this.helpText && typeof this.helpText !== 'undefined',
      'anchor-c-textarea--no-resize': this.noResize,
    };

    const textareaAttributes = {
      'id': this.uuid,
      'aria-required': this.required,
      'aria-live': this.ariaLive,
      'aria-describedby': this.ariaDescribedBy || `${this.uuid}__messages`,
      'disabled': this.disabled,
      'autocomplete': this.autocomplete || undefined,
      'maxlength': this.maxlength,
      'name': this.name,
      'dirname': `${this.name}.dir`,
      'onChange': (event) => this.handleChange(event),
      'onFocus': e => this.handleFocus(e),
      'onBlur': e => this.blurevent.emit(e),
      'placeholder': this.placeholder,
      'readonly': this.readonly,
      'ref': el => {
        this.textareaElm = el as HTMLTextAreaElement;
      },
      'spellcheck': this.spellCheck,
      'autocorrect': this.autocorrect,
      'autofocus': this.autofocus,
    };

    return (
      <Host>
        <div class={textareaContainerClass}>
          <div class="anchor-c-textarea__headarea">
            {this.label && typeof this.label !== 'undefined' ? (
              <anchor-label for={this.uuid}>
                <div class="anchor-c-textarea__label">
                  <div class="anchor-c-textarea__label-text">{this.label}</div>
                </div>
              </anchor-label>
            ) : null}
          </div>
          {this.helpText && <span class="anchor-c-textarea__help-msg">{this.helpText}</span>}
          {(this.hasErrorSlotActive || this.hasError) && (
            <span id={`${this.uuid}__messages`} class={`anchor-c-textarea__messages anchor-c-textarea__messages--${this.messagePlacement}`}>
              <span class="anchor-c-textarea__error-msg">
                {!this.hasErrorSlotActive && this.error}
                <slot name="error-msg"></slot>
                <anchor-icon color="inherit" size="small" name="alert-circle"></anchor-icon>
              </span>
            </span>
          )}
          <div class="anchor-c-textarea__wrapper">
            <textarea class={textareaClass} onKeyUp={() => this.getCurrentlength()} {...textareaAttributes}>
              {this.value}
            </textarea>
          </div>
          {this.maxlength && typeof this.maxlength === 'number' && (
            <span class="anchor-c-textarea__maxlength">
              {this.charcount} / {this.maxlength}
            </span>
          )}
        </div>
      </Host>
    );
  }
}
